import { cloneDeep } from 'lodash';

export interface Inventory {
    alert_threshold: number;
    inventory: number;
    inventory_id: string;
    warehouse_id: string;
    warehouse_name: string;
}

export interface ProductInventory {
    product_id: string;
    title: string;
    product_type: ProductTypeEnum;
    vendor: string;
    shop_name: string;
    shop_id: any;
    image: string;
    sku_list: string[];
    warehouse_list: string[];
    active: boolean;
}

export interface SkuInventory {
    id: string | number;
    sku: string;
    inventory_list: Inventory[];
    variant_json: any;
}

export interface ProductInventoryFilterItem {
    id: string;
    name: string;
}

export enum KeysUsedForFilterEnum {
    TYPE = 'product_type',
    BRAND = 'vendor',
    WAREHOUSE = 'warehouse_id',
}

export enum ProductTypeEnum {
    PRODUCT = 'product',
    CUSTOM_PRODUCT = 'customize_product',
    STANDARD_PRODUCT = 'standard_product',
    DIY_PRODUCT = 'diy_product',
    POD_PRODUCT = 'pod_product',
    WRAPPER = 'wrapper',
    INSERT = 'insert',
    PACKAGE = 'package',
    STICKER = 'sticker',
}

export const ProductTypeMap = new Map([
    [ProductTypeEnum.PRODUCT, 'Product'],
    [ProductTypeEnum.STANDARD_PRODUCT, 'Standard Product'],
    [ProductTypeEnum.CUSTOM_PRODUCT, 'Custom Product'],
    [ProductTypeEnum.DIY_PRODUCT, 'DIY Product'],
    [ProductTypeEnum.POD_PRODUCT, 'POD Product'],
    [ProductTypeEnum.WRAPPER, 'Wrapper'],
    [ProductTypeEnum.INSERT, 'Insert'],
    [ProductTypeEnum.PACKAGE, 'Package'],
    [ProductTypeEnum.STICKER, 'Sticker'],
]);

export class ModalConfiguration<FormDataType> {
    visible = false;
    confirmLoading = false;
    confirmDisable: any; // boolean || ()=>void
    formData: FormDataType;

    private initialFormData: any;

    constructor(params: { visible?: boolean; confirmLoading?: boolean; confirmDisable?: any; formData?: FormDataType }) {
        const { visible, confirmLoading, confirmDisable, formData } = params;

        if (visible !== undefined) {
            this.visible = visible;
        }

        if (confirmLoading !== undefined) {
            this.confirmLoading = confirmLoading;
        }

        this.confirmDisable = confirmDisable;
        this.formData = formData;
        this.initialFormData = cloneDeep(this.formData);
    }

    resetFormData() {
        this.formData = cloneDeep(this.initialFormData);
    }
}

export enum OrderStatusEnum {
    OPEN = 'open',
    CANCELED = 'canceled',
}

export enum WarehouseStatusEnum {
    FAILED = 'failed',
    SUCCESS = 'success',
}

export interface OrderItem {
    order_id: string;
    created_at: string;
    financial_status: string;
    fulfillment_status: string;

    line_items_count: string;
    name: string;
    order_type: string;
    original_shop_id: string;
    original_shop_name: string;
    original_order_id: string;
    total_price: number;
    updated_at: string;
    warehouse_id: string;
    warehouse_name: string;
    order_status: OrderStatusEnum;
    warehouse_order_status: WarehouseStatusEnum;
    last_tracked_at: string;
    delivered_at: string;
    fulfillment_created_at: string;
    shipping_cost: number;

    // Only frontEnd
    shippingStatus?: 'normal' | 'abnormal';
    tags: string;
    fulfillment_wait_time?: number;
}

export interface OrderDetail extends OrderItem {
    order_detail: any;
    original_order_detail: any;
    line_items: {
        id: number;
        image: string;
        product_id: string;
        product_type: ProductTypeEnum;
        sku: string;
        title: string;
        vendor: string;
        price: number;
        quantity: number;
        total_discount: number;
        has_sufficient_inventory: boolean;
        inventory?: {
            sf_warehouse: number;
            hz_warehouse: number;
        };
    }[];
    resolutions?: any[];
    internal_order_status?: string;
    exception?: {
        created_at?: string;
        created_by?: string;
        customer_care?: boolean;
        exception_note?: string;
        exception_status?: InternalOrderStatusEnum;
        resolution_note?: string;
        resolution_status?: string;
    };
}

export interface ShopItem {
    created_at: string;
    email: string;
    name: string;
    phone: string;
    shop_detail: any;
    shop_id: number;
    shop_owner: string;
    shop_type: string;
    updated_at: string;
    account_manager: string;
    sourcing_manager: string;
    is_occupied: boolean;
    setup_complete: boolean;
    subscription_plan: string;
    testing_account: boolean;
    add_payment_method: boolean;
}

export interface ShopSettingsDetail extends ShopItem {
    label_list: any;
    name: string;
    domain: string;
    amazon_affiliate_link: string;
    notes: string;
    id: number;
    ship_to: string;
    user_id: string;
    theme_id: number;
    invitation_code: {
        invitation_code: string;
        is_redeemed: boolean;
    };
    shop_packages: {
        package_type: ProductTypeEnum;
        package_product_sku: string;
        package_product_title: string;
        package_product_image: string;
        is_default: boolean;
        is_selected: boolean;
    }[];
}

export interface AmazonSourcingDetail {
    id: number;
    image: string[];
    product_name: string;
    product_link: string;
    created_at: string;
    created_by: string;
}

export interface ShopifyProductDetail {
    admin_graphql_api_id: string;
    body_html: string;
    created_at: string;
    handle: string;
    id: number;
    product_id?: number;
    image: ShopifyImage;
    images: ShopifyImage[];
    options: any[];
    product_type: string;
    published_at: string;
    published_scope: string;
    tags: string;
    template_suffix: string;
    title: string;
    updated_at: string;
    variants: any[];
    label_list?: any[];
    vendor: string;
    commission?: number;
    discount?: number;
    active: boolean;
    editable?: boolean;
}

export interface ShopifyImage {
    admin_graphql_api_id: string;
    alt: string;
    created_at: string;
    height: number;
    id: number;
    position: number;
    product_id: number;
    src: string;
    updated_at: string;
    variant_ids: any[];
    width: number;
}

export interface ProductInfo {
    img_url: string;
    title: string;
    sub_title?: string;
    sku?: string;
    product_id: number | any;
    product_type: ProductTypeEnum;
    product_price?: number;
    product_quantity?: number;
    has_sufficient_inventory?: boolean;
    inventory?: {
        hz_warehouse: number;
        sf_warehouse: number;
    };
}

export interface SKUItem {
    id: string | number;
    image: string;
    image_object: {
        internal_id?: any;
        path?: string;
        src: string;
    };
    editing?: boolean;
    show_in_shop: boolean;
    is_pending_submit_sku?: boolean;
    inventory_list: Inventory[];
    sku: string;
    variant_json: any;
    compare_at_price: number;
    price: number;
    quantity: number;
    unit_cost: number;
    weight_in_gram: number;
    height_in_cm: number;
    width_in_cm: number;
    depth_in_cm: number;
    package_height_in_cm: number;
    package_width_in_cm: number;
    package_depth_in_cm: number;
    option1?: any;
    option2?: any;
    option3?: any;
    sku_sourcing_urls?: { value: string }[];
    quote_cost?: number;
    warehouse_operation_cost?: number;
    shipping_cost?: number;
}

export interface ProductInventoryDetail {
    body_html: string;
    images: any[];
    image: string;
    product_id: string;
    product_type: ProductTypeEnum;
    title: string;
    vendor: string;
    shop_name: string;
    shop_id: number;
    sku_list: SKUItem[];
    product_sourcing_urls: { value: string }[];
    product_json: any;
    options: any[];
    packaging: any[];
    active: boolean;
    batch_order_record: any;
    required_date: string;
    requested_date: string;
    batch_required_date: string;
    batch_required_warehouse_name: string;
    batch_required_warehouse_id: any;
    listing_status: 'unpublished' | 'review_in_progress' | 'published';
    sales_type: 'limited' | 'evergreen';
    new_labels: number[];
    label_list?: any[];
    standard_product_id: number;
}

export interface UploadedFile {
    url: string;
    path: string;
    type: 'image' | 'video';
    internal_id?: any;
}

export interface SkuListItem {
    id: number;
    image: string;
    product_id: string;
    product_name: string;
    product_sku: string;
    shop_id: number;
    shop_name: string;
    variant_image: string;
    variant_name: string;
    vendor: string;
}

export interface VariantOption {
    title: string;
    price: number;
    unit_cost: number;
    currency: string;
    quote_cost: number;
    quote_currency: string;
    deleted?: boolean;
    option1?: string;
    option2?: string;
    option3?: string;
    sku_sourcing_urls: { value: string }[];
}

export interface OutOfStockListItem {
    in_stock_quantity: number;
    in_transit_quantity: number;
    product_id: string;
    product_name: string;
    sku: string;
    unfulfilled_quantity: number;
    variant_image: string;
    variant_name: string;
    product_sourcing_urls?: any[];
}

export interface PendingOrderListItem {
    created_at: string;
    id: number;
    name: string;
    shop_id: number;
    shop_name: string;
    warehouse_name: string;
    waiting_time?: number;
}

export interface unfulfilledOrderItem {
    created_at: string;
    fulfillment_created_at: string;
    id: number;
    last_tracked_at: string;
    name: string;
    order_status: string;
    order_type: string;
    resolutions: any[];
    shop_id: number;
    shop_name: string;
    warehouse_created_at: string;
    warehouse_name: string;
    not_shipped_waiting_time?: number;
    custom_waiting_time?: number;
    exception?: {
        created_at: string;
        created_by: string;
        customer_care: boolean;
        exception_note: string;
        exception_status: string;
        resolution_note: string;
        resolution_status: string;
    };
}

export interface perShopOrderItem {
    shop_name: string;
    order_total: number;
    undivided_order: number;
    normal_order: number;
    preorder_order: number;
    test_order: number;
}

export enum SampleRequestStatusEnum {
    INITIATED = 'initiated',
    SENT_TO_VENDOR = 'sent_to_vendor',
    CONFIRM_SENT = 'confirm_sent',
    SAMPLE_IN_PROGRESS = 'sample_in_progress',
    DELIVER_TO_OFFICE = 'deliver_to_office',
    PRODUCT_SHIPPED = 'product_shipped',
}

export const SampleRequestStatusText = new Map([
    [SampleRequestStatusEnum.INITIATED, 'SAMPLE_PROGRESS_INITIATED'],
    [SampleRequestStatusEnum.SENT_TO_VENDOR, 'SAMPLE_PROGRESS_SENT_TO_VENDOR'],
    [SampleRequestStatusEnum.CONFIRM_SENT, 'SAMPLE_PROGRESS_CONFIRM_SENT'],
    [SampleRequestStatusEnum.SAMPLE_IN_PROGRESS, 'SAMPLE_PROGRESS_SAMPLE_IN_PROGRESS'],
    [SampleRequestStatusEnum.DELIVER_TO_OFFICE, 'SAMPLE_PROGRESS_DELIVER_TO_OFFICE'],
    [SampleRequestStatusEnum.PRODUCT_SHIPPED, 'SAMPLE_PROGRESS_PRODUCT_SHIPPED'],
]);

export interface Influencer {
    userId: string;
    status: string;
    city: string;
    country: string;
    gender: string;
    ageGroup: string;
    profile: any;
    stats: any;
    audience: InfluencerAudience;
    popularPosts: any[];
    recentPosts: any[];
    sponsoredPosts: any[];
    hashtags: any[];
    mentions: any[];
    contacts: any[];
    is_registered: boolean;
    complete_campaign: boolean;
    in_campaign: boolean;
    in_list: boolean;
    register_email: string;
    label_list: any;
}

export interface InfluencerAudience {
    credibility: number;
    notable: number;
    genders: InfluencerDistributionItem[];
    geoCities: InfluencerDistributionItem[];
    geoCountries: InfluencerDistributionItem[];
    ages: InfluencerDistributionItem[];
    gendersPerAge: InfluencerGenderDistributionItem[];
    brandAffinity: InfluencerDistributionItem[];
    interests: InfluencerDistributionItem[];
    audienceLookalikes: any[];
}

export interface InfluencerDistributionItem {
    code?: string;
    name?: string;
    weight: number;
}

export interface InfluencerGenderDistributionItem {
    code?: string;
    name?: string;
    male: number;
    female: number;
}
export interface SamplePurchaseItem {
    id?: number;
    product_name: string;
    shop_id: any;
    request_qty?: number;
    request_quantity?: number;
    sourcing_link: string;
    address_id?: string;
    address?: string;
    instruction: string;
    address_detail?: any;
    urgency: string;
    required_date: string;
    qc_required: boolean;
    live_demo_requried: boolean;
    photo_shoot_required: boolean;
    purchase_complete?: boolean;
    note_history?: any[];
    estimate_date?: string;
    estimate_status?: string;
}

export interface ExceptionOrder {
    id: string;
    order_type?: string;
    delivered_at?: string;
    exception?: {
        exception_status?: string;
        exception_note?: string;
        resolution_status?: string;
        resolution_note?: string;
        customer_care?: boolean;
    };
}

export interface AddressItem {
    id?: number;
    name: string;
    first_name: string;
    last_name: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone_number: string;
}

export const OrderTypeText = new Map([
    [null, '未划分'],
    ['undivided', '未划分'],
    ['test', '测试订单'],
    ['preorder', '预售订单'],
    ['normal', '常规订单'],
]);

export enum InternalOrderStatusEnum {
    FAILED_NO_PURCHASE = 'failed_no_purchase',
    FAILED_IN_TRANSIT = 'failed_in_transit',
    LATE_FULFILLMENT = 'late_fulfillment',
    LATE_SHIPMENT = 'late_shipment',
    FULFILLMENT_NOT_UPDATE = 'fulfillment_not_update',
    OTHER = 'other',
}

export const InternalOrderStatusText = new Map([
    [InternalOrderStatusEnum.FAILED_NO_PURCHASE, '缺货，未采购'],
    [InternalOrderStatusEnum.FAILED_IN_TRANSIT, '缺货，采购在途'],
    [InternalOrderStatusEnum.LATE_FULFILLMENT, '仓库发货超时'],
    [InternalOrderStatusEnum.LATE_SHIPMENT, '物流时效超时'],
    [InternalOrderStatusEnum.FULFILLMENT_NOT_UPDATE, '物流节点2天未更新'],
    [InternalOrderStatusEnum.OTHER, '其他'],
]);

export const InternalOrderStatusList = [
    { value: InternalOrderStatusEnum.FAILED_NO_PURCHASE, name: '缺货，未采购' },
    { value: InternalOrderStatusEnum.FAILED_IN_TRANSIT, name: '缺货，采购在途' },
    { value: InternalOrderStatusEnum.LATE_FULFILLMENT, name: '仓库发货超时' },
    { value: InternalOrderStatusEnum.LATE_SHIPMENT, name: '物流时效超时' },
    { value: InternalOrderStatusEnum.FULFILLMENT_NOT_UPDATE, name: '物流节点2天未更新' },
    { value: InternalOrderStatusEnum.OTHER, name: '其他' },
];

export const ResolutionList = [
    '已通知采购人员采购',
    '已查询采购进度',
    '仓库爆仓，已跟进发货',
    '仓库丢失，已报备采购',
    '仓库损毁，已报备采购',
    '仓库漏发，已通知发出',
    '物流节点未更新，已询承运商',
    '物流节点未更新，实际已到',
    '物流异常，清关问题',
    '到达待取，已通知客户取货',
    '物流异常，货物损毁或丢件',
    '物流异常，收件信息不正确',
    '其他',
];

export const CheckRequirementOptions = [
    { label: '是否需要QC及QC照片', value: 'qc_required', checked: false },
    { label: '是否需要直播验货', value: 'live_demo_requried', checked: false },
    { label: '是否需要国内拍摄上线内容', value: 'photo_shoot_required', checked: false },
];

export enum GroupTypeEnum {
    BY_MONTH = 'by_month',
    BY_WEEK = 'by_week',
    BY_DAY = 'by_day',
}

export enum SubscriptionEnum {
    FREE = 'starter',
    ADVANCED = 'advanced',
    PREMIUM = 'premium',
}

export enum ProjectStatusEnum {
    DRAFT = 'draft',
    PENDING = 'pending',
    UNVERIFIED = 'unverified',
    ACTIVE = 'active',
}

export interface NFTProjectItem {
    chain: string;
    contract_address: string;
    contract_image_url: string;
    contract_name: string;
    contract_list: NFTContractItem[];
    created_at: string;
    created_by: string;
    description: string;
    id: number;
    link: string;
    merch_options: number[];
    name: string;
    project_key: string;
    resource: {
        banner_bg: string;
        brand_avatar: string;
        browserLogo: string;
        footerLogo: string;
        headerLogo: string;
        loadingIcon: string;
    };
    social_medias: { link: string; name: string }[];
    updated_at: string;
    updated_by: string;
    shop_id: number;
    status: ProjectStatusEnum;
    campaign_count?: number;
    internal_info?: ProjectInternalInfo;
    user_id?: string;
    project_type: string;
    banner?: {
        text?: string;
        link_text?: string;
        link_route?: string;
    };
    email: string;
}

export interface NFTContractItem {
    chain: string;
    collection_id: number;
    contract_address: string;
    contract_image_url: string;
    contract_name: string;
    id: number;
    project_id: number;
    status: ProjectStatusEnum;
    name: string;
    nft_type: string;
    verification_record?: {
        content: string;
        created_at: string;
        created_by: string;
        id: number;
        project_collection_id: number;
        status: string;
        updated_at: string;
        updated_by: string;
        verified_twitter: string;
    };
}

export interface CreateProject {
    id?: number;
    project_key: string;
    name: string;
    contract_name: string;
    contract_address: string;
    description: string;
    link: string;
    status?: string;
    social_medias: { name: string; link: string }[];
    resource: {
        brand_avatar: string;
        banner_bg: string;
        loadingIcon: string;
    };
    banner: {
        text?: string;
        link_text?: string;
        link_route?: string;
    };
    email: string;
    project_type: 'normal' | 'gamify';
}

export interface NFTPackageItem {
    createdAt: string;
    createdBy: string;
    deleted: boolean;
    downloadUrl: string;
    id: number;
    includeOrders: number[];
    packageName: string;
    packagingTime: string;
    updatedAt: string;
    updatedBy: string;
    uploadTime: string;
    batches: NFTBatchItem[];
    startName: string;
    endName: string;
    totalBatches?: number;
    totalOrders?: number;
}

export interface NFTBatchItem {
    batchIndex: number;
    batchName: string;
    batchOrderCount: number;
    id: number;
    includeOrders: number[];
    optionId: number;
    packageId: number;
    packageName: string;
}

export interface NFTBatchOrderItem {
    addressDetail: {
        address_line_1: string;
        address_line_2: string;
        city: string;
        country: string;
        email: string;
        first_name: string;
        last_name: string;
        phone_number: string;
        state: string;
        zip: string;
    };
    contractAddress: string;
    contractName: string;
    createdAt: string;
    createdBy: string;
    id: number;
    imagePackage: { src?: string; b64?: string; type: string }[];
    orderName: string;
    optionId: number;
    owner: string;
    productOptions: { id: number; options: { name: string; value: string }[] }[];
    shopId: number;
    shopifyOrderId: number;
    tokenId: string;
    updatedAt: string;
    updatedBy: string;
    walletAddress: string;
}

export interface ProductPurchaseItem {
    airtable_key: string;
    arrived_quantity: number;
    barcode: string;
    carrier: string;
    check_requirement: string;
    chinese_declaration_name: string;
    created_at: string;
    created_by: string;
    currency: string;
    deliver_eta: string;
    destination_address: string;
    english_declaration_name: string;
    exceptions: any[];
    id: number;
    in_stock_quantity: number;
    is_kit_item: boolean;
    latest_shipping_status: string;
    logistics: {
        carrier: string;
        destination_address: string;
        has_exception: boolean;
        id: number;
        tracking_number: string;
    }[];
    nft_batch_id: number;
    order_content: string;
    product_id: string;
    product_name: string;
    product_sku: string;
    purchase_complete: boolean;
    purchase_date: string;
    purchase_order_id: string;
    purchase_platform: string;
    purchased_quantity: number;
    qc_ratio: number;
    qc_requirement: string;
    qc_result: any;
    quantity: number;
    restock_request_id: number;
    shipping_cost: number;
    shop_id: number;
    shop_name: string;
    testing_account: boolean;
    tracking_number: string;
    unit_cost: number;
    updated_at: string;
    updated_by: string;
    variant_image: string;
    variant_name: string;
    kitVisible?: boolean;
    img_base?: any;
}

export interface FrontEndNFTProductItem {
    product_id?: number;
    name: string;
    price: number;
    compare_at_price: number;
    additional_shipping_cost: number;
    option_image_url: string;
    is_nft_merch: boolean;
    sales_type: string;
    shop_name?: string;
    shop_id: number;
    description: string;
    id?: number;
    image_url: string;
    logo_templates?: any[];
    options: { name: string; values: string[] }[];
    placeholder_image: { path: string; src: string };
    preview_images: {
        background_height?: number;
        background_path: string;
        background_src: string;
        background_width?: number;
        foreground_path?: string;
        foreground_src?: string;
        id?: number;
        logo_height?: number;
        logo_left?: number;
        logo_top?: number;
        logo_width?: number;
        name: string;
    }[];
    size_chart: { src: string; path: string; device: 'desktop' | 'mobile' }[];
    product_images: { src: string; path: string }[];
    tags: string[];
    template_id?: number;
    product_name: string;
    rowspan?: number;
}

export interface NFTProductItem {
    additional_shipping_cost: number;
    compare_at_price: number;
    id: number;
    image_url: string;
    is_nft_merch: boolean;
    name: string;
    price: number;
    product_list: {
        description: string;
        id: number;
        image_url: string;
        logo_templates: any[];
        options: { name: string; values: string[] }[];
        placeholder_image: { path: string; src: string };
        preview_images: {
            background_height: number;
            background_path: string;
            background_src: string;
            background_width: number;
            foreground_path: string;
            foreground_src: string;
            id: number;
            logo_height: number;
            logo_left: number;
            logo_top: number;
            logo_width: number;
            name: string;
        }[];
        product_images: { src: string; path: string }[];
        product_name: string;
        size_chart: { src: string; path: string; device: 'desktop' | 'mobile' }[];
        tags: string[];
        template_id: number;
    }[];
    sales_type: string;
    shop_id: number;
    shop_name: string;
}

export interface editNFTProduct {
    id?: number;
    name: string;
    price: number;
    product_list: {
        description: string;
        id?: number;
        image_url: string;
        logo_templates?: any[];
        options: { name: string; values: string[] }[];
        placeholder_image: { path: string; src: string };
        preview_images: {
            background_height?: number;
            background_path: string;
            background_src: string;
            background_width?: number;
            foreground_path?: string;
            foreground_src?: string;
            id?: number;
            logo_height?: number;
            logo_left?: number;
            logo_top?: number;
            logo_width?: number;
            name: string;
        }[];
        tags: string[];
        template_id?: number;
        product_name: string;
    }[];
}

export interface NftOptionExportConfig {
    backImage: string;
    backInfo: string;
    brand: string;
    createdAt?: string;
    createdBy?: string;
    frontImage: string;
    frontInfo: string;
    id?: number;
    nameCn: string;
    nameEn: string;
    optionId?: number;
    prodRequirement: string;
    purchaseInfo: string;
    purchasePriceCny: number;
    sizeMapping: { tshe: string; w3w: string }[];
    templateId: number;
    updatedAt?: string;
    updatedBy?: string;
}

export interface WarehouseItem {
    warehouse_id: number;
    warehouse_name: string;
}

export enum CampaignTypeEnum {
    FREE_MINT = 'free_mint',
    WHITELIST = 'whitelist',
    AIRDROP = 'airdrop',
    GIVEAWAY = 'giveaway',
}

export enum EligibleTypeEnum {
    PUBLIC = 'generic',
    HOLDERS_ONLY = 'member_only',
    SPECIFIC_ADDRESS = 'airdrop_only',
}

export enum NetworkEnum {
    ETHEREUM = 'eth',
    SOLANA = 'solana',
}

export class MediaFile {
    src: string;
    path: string;
}

export enum SocialMedia {
    DISCORD = 'discord',
    TWITTER = 'twitter',
    TELEGRAM = 'telegram',
    YOUTUBE = 'youtube',
    REDDIT = 'reddit',
    EMAIL = 'email',
}

export enum NFTPlatform {
    DAPPER = 'dapper',
    BLOCTO = 'blocto',
    WALLET_CONNECT = 'wallet_connect',
    META_MASK = 'meta_mask',
    COINBASE = 'coinbase',
    PHANTOM = 'phantom',
    NEAR = 'near',
    UNSTOPPABLE_DOMAINS = 'unstoppable_domains',
}

interface Resource extends MediaFile {
    required_chain?: {
        chainId: string;
        chainName: string;
        rpcUrls: string[];
        nativeCurrency: {
            symbol: string;
            decimals: number;
        };
    };
}

export enum CampaignStatus {
    ACTIVE = 'ACTIVE',
    ENDED = 'ENDED',
    UPCOMING = 'UPCOMING',
}

export interface CampaignItem {
    id: number;
    project_id: number;
    type: CampaignTypeEnum;
    name: string;
    description: string;
    start_date: string;
    end_date: string;
    images: MediaFile[];
    sales_type: EligibleTypeEnum;
    required_social_media: { type: SocialMedia; is_required: boolean }[];
    campaign_challenges: {
        challenge_type: SocialMedia;
        check_type: 'follow' | 'retweet' | null;
        challenge_input: string;
    }[];
    require_retweet: boolean;
    require_wallet: boolean;
    supported_wallet: NFTPlatform[];
    is_public: boolean;
    is_deleted: boolean;
    resource: Resource; // whitelist CSV file
    collection_id?: any;
    chain: NetworkEnum;
    status?: CampaignStatus;
    entry_count?: number;
    created_at: string;
}

export const CampaignTypeMap = new Map([
    [CampaignTypeEnum.AIRDROP, 'AirDrop'],
    [CampaignTypeEnum.FREE_MINT, 'Free Mint'],
    [CampaignTypeEnum.WHITELIST, 'Whitelist'],
    [CampaignTypeEnum.GIVEAWAY, 'Giveaway'],
]);

export const EligibleTypeMap = new Map([
    [EligibleTypeEnum.PUBLIC, 'Available to all'],
    [EligibleTypeEnum.HOLDERS_ONLY, 'Holders Only'],
    [EligibleTypeEnum.SPECIFIC_ADDRESS, 'Special Members Only'],
]);

export interface PerkItem {
    created_at: string;
    created_by: string;
    description: string;
    id: number;
    is_deleted: boolean;
    link: string;
    logo: string;
    name: string;
    order: number;
    type: string;
    updated_at: string;
    updated_by: string;
}

export interface ApplicationItem {
    created_at: string;
    id: number;
    perk_id: number;
    perk_name: string;
    project_id: number;
    project_name: string;
}

export interface ProjectInternalInfo {
    type: InternalInfoType;
    source: string;
    twitter_follower_count: number;
    discord_member_count: number;
}

export enum InternalInfoType {
    INBOUND = 'inbound',
    OUTBOUND = 'outbound',
    PARTNER = 'partner',
}

export interface ProjectReportItem {
    date: string;
    project_campaign_created: number;
    project_collection_added: number;
    project_shop_opened: number;
    total_project: number;
    project_discord_connected: number;
}

export interface ReportEngagement {
    project_created: number;
    collection_added: number;
    campaign_created: number;
    airdrop_sent: number;
    address_imported: number;
    tag_added: number;
    email_sent: number;
    filter_tag_created: number;
    discord_role_created: number;
    '3rd_party_role_added': number;
}

export interface ReportActivityItem {
    id: number;
    event_count: number;
    name: string;
    project_created: string[];
    collection_added: string[];
    campaign_created: string[];
    airdrop_sent: string[];
    address_imported: string[];
    tag_added: string[];
    email_sent: string[];
    filter_tag_created: string[];
    discord_role_created: string[];
    '3rd_party_role_added': string[];
}

export interface ProjectActivityItem {
    '3rd_party_role_added': string[];
    address_imported: string[];
    airdrop_sent: string[];
    campaign_created: string[];
    collection_added: string[];
    discord_role_created: string[];
    email_sent: string[];
    event_count: number;
    filter_tag_created: string[];
    month: string;
    project_created: string[];
    tag_added: string[];
}
